import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';
import { useLab } from '../i18n/LabContext';

const S = {
  page: { padding: '0 0 32px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--text)' },
  sub: { fontSize: 12, color: 'var(--text3)', marginTop: 4 },
  labSelect: {
    background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)',
    padding: '8px 14px', borderRadius: 10, fontSize: 13, cursor: 'pointer'
  },
  refreshBtn: {
    background: 'rgba(0,212,255,0.08)', border: '1px solid var(--cyan)', color: 'var(--cyan)',
    padding: '8px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 },

  // PC karta
  card: (status, booked) => ({
    borderRadius: 16, padding: 16, border: '1px solid',
    borderColor: status === 'available' && booked ? 'var(--orange)'
      : status === 'available' ? 'var(--green)'
      : status === 'busy' ? 'var(--red)' : 'var(--border)',
    background: status === 'available' && booked ? 'rgba(255,107,53,0.06)'
      : status === 'available' ? 'rgba(0,255,136,0.04)'
      : status === 'busy' ? 'rgba(255,59,92,0.04)' : 'var(--card2)',
    cursor: status !== 'broken' ? 'pointer' : 'default',
    transition: 'all 0.2s',
    position: 'relative',
  }),
  pcNum: { fontFamily: 'Orbitron, sans-serif', fontSize: 13, color: 'var(--text2)', marginBottom: 6 },
  statusDot: (status, booked) => ({
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6,
    background: status === 'available' && booked ? 'var(--orange)'
      : status === 'available' ? 'var(--green)'
      : status === 'busy' ? 'var(--red)' : 'var(--text3)',
  }),
  statusLabel: (status, booked) => ({
    fontSize: 12, fontWeight: 600,
    color: status === 'available' && booked ? 'var(--orange)'
      : status === 'available' ? 'var(--green)'
      : status === 'busy' ? 'var(--red)' : 'var(--text3)',
  }),
  userName: { fontSize: 14, fontWeight: 700, color: 'var(--text)' },
  pkgName: { fontSize: 11, color: 'var(--cyan)' },
  timeLeft: (mins) => ({
    fontFamily: 'Orbitron, sans-serif', fontSize: 18, fontWeight: 700, marginTop: 8,
    color: mins < 10 ? 'var(--red)' : mins < 30 ? 'var(--orange)' : 'var(--text)',
  }),
  cardBtn: (color) => ({
    width: '100%', marginTop: 10, padding: '7px 0',
    background: `rgba(${color},0.08)`, border: `1px solid rgb(${color})`,
    color: `rgb(${color})`, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600
  }),

  // Modal
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100
  },
  modal: {
    background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 20,
    padding: 32, width: 400, maxHeight: '80vh', overflowY: 'auto'
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 20 },
  label: { display: 'block', fontSize: 11, color: 'var(--text2)', letterSpacing: 1, marginBottom: 6 },
  input: {
    width: '100%', background: 'var(--card2)', border: '1px solid var(--border)',
    color: 'var(--text)', padding: '12px 14px', borderRadius: 10, fontSize: 14,
    outline: 'none', boxSizing: 'border-box', marginBottom: 14
  },
  select: {
    width: '100%', background: 'var(--card2)', border: '1px solid var(--border)',
    color: 'var(--text)', padding: '12px 14px', borderRadius: 10, fontSize: 14,
    outline: 'none', boxSizing: 'border-box', marginBottom: 14
  },
  row: { display: 'flex', gap: 10, marginTop: 4 },
  btnPrimary: {
    flex: 1, padding: '12px 0', background: 'linear-gradient(135deg, var(--purple), var(--cyan))',
    border: 'none', color: '#fff', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700
  },
  btnCancel: {
    padding: '12px 20px', background: 'transparent', border: '1px solid var(--border)',
    color: 'var(--text2)', borderRadius: 10, cursor: 'pointer', fontSize: 14
  },
  btnDanger: {
    flex: 1, padding: '12px 0', background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)',
    color: 'var(--red)', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700
  },
  btnWarning: {
    flex: 1, padding: '12px 0', background: 'rgba(255,107,53,0.1)', border: '1px solid var(--orange)',
    color: 'var(--orange)', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700
  },
  divider: { borderColor: 'var(--border)', margin: '16px 0' },
  info: { fontSize: 12, color: 'var(--text2)', marginBottom: 4 },
  infoVal: { color: 'var(--text)', fontWeight: 600 },
};

function fmtTime(endsAt) {
  const diff = new Date(endsAt) - new Date();
  if (diff <= 0) return '00:00';
  const total = Math.floor(diff / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function minsLeft(endsAt) {
  return Math.floor((new Date(endsAt) - new Date()) / 60000);
}

export default function Floor() {
  const { t } = useLang();
  const { mode: labMode } = useLab();
  const isAllMode = labMode === 'all';
  const [labs, setLabs] = useState([]);
  const [labId, setLabId] = useState('');
  const [packages, setPackages] = useState([]);
  const [computers, setComputers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [modal, setModal] = useState(null);
  // modal = { type: 'open'|'manage'|'addtime'|'move', computer, session? }

  // Form state
  const [guestName, setGuestName] = useState('');
  const [payType, setPayType] = useState('cash');
  const [selPkg, setSelPkg] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [moveTo, setMoveTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [cmdSending, setCmdSending] = useState(false);
  const [sessionMode, setSessionMode] = useState('package'); // 'package' | 'hourly'
  const [clientPhone, setClientPhone] = useState('');
  const [clientFound, setClientFound] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupDone, setTopupDone] = useState(false);
  const [topupIsDebt, setTopupIsDebt] = useState(false);
  const [payMethod, setPayMethod] = useState('cash');
  const [extendFromBalance, setExtendFromBalance] = useState(false);
  // Bron forma
  const [bookName, setBookName] = useState('');
  const [bookPhone, setBookPhone] = useState('');
  const [bookDate, setBookDate] = useState('');
  const [bookFrom, setBookFrom] = useState('');
  const [bookTo, setBookTo] = useState('');
  const [bookPkg, setBookPkg] = useState('');
  const [bookSaving, setBookSaving] = useState(false);
  const [floorZoom, setFloorZoom] = useState(1);
  const floorScrollRef = useRef(null);
  // Station (bilyard/PS) state
  const [stationTypeFilter, setStationTypeFilter] = useState('all');
  const [stationMode, setStationMode] = useState('hourly_open');
  const [players, setPlayers] = useState(2);
  const [selRate, setSelRate] = useState('');
  const [stationRates, setStationRates] = useState([]);
  const [tipAmount, setTipAmount] = useState('');
  const [stopResult, setStopResult] = useState(null);

  async function sendCommand(pcId, command, payload = {}) {
    setCmdSending(true);
    try {
      await api.post(`/owner/computers/${pcId}/command`, { command, payload });
    } catch (e) {
      alert(e.response?.data?.error || 'Buyruq yuborishda xatolik');
    } finally { setCmdSending(false); }
  }

  async function sendWoL(pcId) {
    try {
      await api.post(`/owner/computers/${pcId}/wol`);
      alert('WoL signal yuborildi!');
    } catch (e) {
      alert(e.response?.data?.error || 'WoL xatolik');
    }
  }

  // Countdown ticker
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const el = floorScrollRef.current;
    if (!el) return;
    function onWheel(e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setFloorZoom(z => Math.min(2, Math.max(0.3, parseFloat((z + delta).toFixed(1)))));
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Load labs
  useEffect(() => {
    api.get('/owner/labs').then(r => {
      setLabs(r.data);
      if (r.data.length) setLabId(localStorage.getItem('selected_lab_id') || String(r.data[0].id));
    });
  }, []);

  const loadFloor = useCallback(() => {
    if (!labId) return;
    api.get(`/owner/floor?lab_id=${labId}`)
      .then(r => { setComputers(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [labId]);

  useEffect(() => { loadFloor(); }, [loadFloor]);

  // Load packages + rooms
  const [roomPositions, setRoomPositions] = useState({});
  useEffect(() => {
    if (!labId) return;
    api.get(`/labs/${labId}`).then(r => setPackages(r.data.packages || []));
    api.get(`/owner/station-rates?lab_id=${labId}`).then(r => setStationRates(r.data)).catch(() => {});
    api.get(`/owner/computers/layout?lab_id=${labId}`)
      .then(r => {
        const rs = r.data.rooms || [];
        setRooms(rs);
        const rp = {};
        rs.forEach(rm => { rp[rm.id] = { x: +rm.pos_x || 20, y: +rm.pos_y || 20, w: +rm.width || 320, h: +rm.height || 260, color_index: rm.color_index ?? 0 }; });
        setRoomPositions(rp);
      })
      .catch(() => {});
  }, [labId]);

  // Auto-refresh (5s)
  useEffect(() => {
    const t = setInterval(loadFloor, 5000);
    return () => clearInterval(t);
  }, [loadFloor]);

  const openModal = (type, computer, session = null) => {
    if (isAllMode) {
      alert('🌐 Hammasi rejimida yozish amallari o\'chirilgan. Yuqoridan klub tanlang.');
      return;
    }
    setModal({ type, computer, session });
    setGuestName('');
    setPayType('cash');
    // Birinchi tanlangan paket: o'sha xonaga tegishli paket
    const roomId = computer?.room_id ?? null;
    const firstAllowed = packages.find(p => {
      const ids = p.room_ids || (p.room_id ? [p.room_id] : []);
      return ids.includes(roomId) || ids.includes(Number(roomId));
    });
    setSelPkg(firstAllowed ? String(firstAllowed.id) : '');
    setMoveTo('');
    setSessionMode('package');
    setClientPhone('');
    setClientFound(null);
    setTopupAmount('');
    setTopupDone(false);
    setTopupIsDebt(false);
    setPayMethod('cash');
    setPayAmount('');
    setExtendFromBalance(false);
    setBookName(''); setBookPhone(''); setBookDate(''); setBookFrom(''); setBookTo(''); setBookPkg(''); setBookSaving(false);
    setStationMode('hourly_open'); setPlayers(2); setSelRate(''); setTipAmount(''); setStopResult(null);
  };

  async function setMaintenance(pcId, broken) {
    try {
      await api.patch(`/owner/computers/${pcId}`, { is_broken: broken, status: broken ? 'maintenance' : 'available' });
      loadFloor();
      closeModal();
    } catch (e) { alert(e.response?.data?.error || 'Xatolik'); }
  }

  function calcMins(amount) {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return 0;
    const s = modal?.session;
    if (!s) return 0;
    // Soatlik sessiya
    if (modal.computer?.hourly_rate) {
      return Math.floor((amt / parseFloat(modal.computer.hourly_rate)) * 60);
    }
    // Paketli sessiya: narx va davomiylik orqali hisoblash
    if (s.package_price && s.duration_minutes) {
      const minPerSom = parseFloat(s.duration_minutes) / parseFloat(s.package_price);
      return Math.floor(amt * minPerSom);
    }
    return 0;
  }

  async function handlePayAndAddTime() {
    if (topupIsDebt) {
      // Qarz sifatida yozish — vaqt emas, qarz jadvaliga
      const amt = parseFloat(payAmount);
      if (!amt || !modal.session.user_id) return;
      setSaving(true);
      try {
        await api.post(`/owner/clients/${modal.session.user_id}/topup`, { amount: amt, is_debt: true });
        setPayAmount('');
        setTopupIsDebt(false);
        closeModal();
        loadFloor();
      } catch (e) {
        alert(e.response?.data?.error || 'Xatolik');
      } finally { setSaving(false); }
      return;
    }
    const amt = parseFloat(payAmount);
    if (!amt || amt < 100) return;
    setSaving(true);
    try {
      await api.post(`/owner/floor/sessions/${modal.session.session_id}/add-balance`, { amount: amt, payment_method: payMethod });
      closeModal();
      loadFloor();
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    } finally { setSaving(false); }
  }

  async function handleCreateBooking() {
    if (!bookDate || !bookFrom) return;
    setBookSaving(true);
    try {
      const scheduled_at = `${bookDate}T${bookFrom}:00`;
      const scheduled_to = bookTo ? `${bookDate}T${bookTo}:00` : null;
      await api.post('/owner/bookings', {
        computer_id: modal.computer.id,
        user_phone: bookPhone || undefined,
        guest_name: bookName || undefined,
        scheduled_at,
        scheduled_to: scheduled_to || undefined,
        package_id: bookPkg || undefined,
      });
      closeModal(); loadFloor();
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    } finally { setBookSaving(false); }
  }

  async function handleTopup() {
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) return;
    const userId = modal?.session?.user_id || modal?.computer?.user_id;
    if (!userId) return;
    setSaving(true);
    try {
      await api.post(`/owner/clients/${userId}/topup`, {
        amount: amt,
        is_debt: topupIsDebt,
        payment_method: topupIsDebt ? undefined : payMethod,
      });
      setTopupDone(true);
      setTopupAmount('');
      loadFloor();
    } catch (e) { alert(e.response?.data?.error || 'Xatolik'); }
    finally { setSaving(false); }
  }

  async function searchClient(phone) {
    setClientPhone(phone);
    setClientFound(null);
    if (phone.length < 9) return;
    try {
      const r = await api.get(`/owner/clients?search=${encodeURIComponent(phone)}`);
      const found = r.data.users?.find(u => u.phone === phone || u.phone === phone.replace(/\s/g, ''));
      setClientFound(found || null);
    } catch (e) { /* ignore */ }
  }

  const closeModal = () => setModal(null);

  const isTimed = modal?.computer?.type && modal.computer.type !== 'pc';
  const isHourlyMode = isTimed || (modal?.computer?.type === 'pc' && sessionMode === 'hourly');

  async function handleOpenSession() {
    setSaving(true);
    try {
      if (isHourlyMode) {
        const isTimed = modal.computer.type && modal.computer.type !== 'pc';
        await api.post('/owner/floor/start-timed', {
          computer_id: modal.computer.id,
          guest_name: guestName || null,
          payment_type: payType,
          user_id: clientFound?.id || null,
          session_mode: isTimed ? stationMode : 'hourly_open',
          rate_id: selRate ? parseInt(selRate) : null,
          players: isTimed ? (parseInt(players) || 1) : 1,
        });
      } else {
        if (!selPkg) return;
        await api.post('/owner/floor/start', {
          computer_id: modal.computer.id,
          package_id: parseInt(selPkg),
          guest_name: clientFound ? clientFound.name : (guestName || 'Naqd mijoz'),
          payment_type: clientFound ? 'balance' : payType,
          user_id: clientFound?.id || null,
          extend_from_balance: extendFromBalance && !!clientFound,
        });
      }
      closeModal();
      loadFloor();
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    } finally { setSaving(false); }
  }

  async function handleStop() {
    if (!window.confirm('Sessiyani to\'xtatmoqchimisiz?')) return;
    setSaving(true);
    try {
      const isTimed = modal.computer.type && modal.computer.type !== 'pc';
      const res = await api.post(`/owner/floor/sessions/${modal.session.session_id}/stop`, {
        tip_amount: isTimed ? (parseFloat(tipAmount) || 0) : 0,
        payment_type: payType,
      });
      if (isTimed && res.data) {
        setStopResult(res.data);
        loadFloor();
      } else {
        closeModal();
        loadFloor();
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    } finally { setSaving(false); }
  }

  async function handleMove() {
    if (!moveTo) return;
    setSaving(true);
    try {
      await api.post(`/owner/floor/sessions/${modal.session.session_id}/move`, {
        new_computer_id: parseInt(moveTo),
      });
      closeModal();
      loadFloor();
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    } finally { setSaving(false); }
  }

  const availablePCs = computers.filter(c => {
    if (c.status !== 'available' || (modal && c.id === modal.computer?.id)) return false;
    const mt = modal?.computer?.type || 'pc';
    if (mt === 'pc') return !c.type || c.type === 'pc';
    return c.type === mt;
  });
  const busyCount = computers.filter(c => c.status === 'busy' && c.session_id).length;
  const freeCount = computers.filter(c => c.status === 'available').length;
  const filteredComputers = stationTypeFilter === 'all' ? computers
    : computers.filter(c => stationTypeFilter === 'pc' ? (!c.type || c.type === 'pc') : c.type === stationTypeFilter);

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>{t('floorTitle')}</div>
          <div style={S.sub}>
            {busyCount} {t('floorBusy')} · {freeCount} {t('floorFree')} · {computers.length} {t('floorTotal')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {false && labs.length > 1 && (
            <select style={S.labSelect} value={labId} onChange={e => setLabId(e.target.value)}>
              {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <button style={S.refreshBtn} onClick={loadFloor}>↻ {t('refresh')}</button>
        </div>
      </div>

      {isAllMode && (
        <div style={{
          background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12,
          padding: '12px 18px', marginBottom: 16, fontSize: 13, color: 'var(--text2)',
        }}>
          🌐 <strong>Hammasi</strong> — faqat ko'rish rejimida. Sessiya boshlash/to'xtatish,
          balans qo'shish, komanda yuborish va boshqa yozish amallari uchun yuqoridan aniq klub tanlang.
        </div>
      )}

      {/* Station type tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {[['all', '🏢 Hammasi'], ['pc', '🖥 PC'], ['billiard', '🎱 Bilyard'], ['playstation', '🎮 PlayStation']].map(([key, label]) => {
          const cnt = key === 'all' ? computers.length : computers.filter(c => key === 'pc' ? (!c.type || c.type === 'pc') : c.type === key).length;
          return (
            <button key={key} onClick={() => setStationTypeFilter(key)} style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)',
              background: stationTypeFilter === key ? 'rgba(0,212,255,0.15)' : 'transparent',
              color: stationTypeFilter === key ? 'var(--cyan)' : 'var(--text3)',
              cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>{label} <span style={{ opacity: 0.6, fontSize: 11 }}>({cnt})</span></button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
        {[[`#22c55e`, t('statusAvailableFloor')], [`#f97316`, t('statusBusyFloor')], [`#f59e0b`, t('statusBooked')], [`#ef4444`, t('statusMaintenance')], [`#374151`, t('statusOffline')]].map(([color, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {label}
          </div>
        ))}
        {rooms.length > 0 && <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>{t('realLayout')}</span>}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--cyan)' }}>{t('loading')}</div>
      ) : rooms.length > 0 && computers.some(c => c.pos_x != null) ? (
        /* ── Canvas ko'rinish: scrollable ── */
        <div ref={floorScrollRef} style={{
          height: 600, overflow: 'auto',
          border: '1px solid var(--border)', borderRadius: 14,
          background: '#0c0c13',
        }}>
          <div style={{ width: 2400 * floorZoom, height: 1800 * floorZoom, position: 'relative' }}>
          <div style={{
            position: 'absolute', width: 2400, height: 1800,
            transform: `scale(${floorZoom})`, transformOrigin: 'top left',
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)',
            backgroundSize: '72px 72px',
          }}>
            {/* Xona zonalari */}
            {rooms.map((rm, idx) => {
              const rp = roomPositions[rm.id];
              if (!rp) return null;
              const FLOOR_PALETTE = [
                { bg: 'rgba(99,102,241,0.07)', border: '#6366f1', text: '#818cf8' },
                { bg: 'rgba(16,185,129,0.07)',  border: '#10b981', text: '#34d399' },
                { bg: 'rgba(245,158,11,0.07)', border: '#f59e0b', text: '#fbbf24' },
                { bg: 'rgba(239,68,68,0.07)',  border: '#ef4444', text: '#f87171' },
                { bg: 'rgba(236,72,153,0.07)', border: '#ec4899', text: '#f472b6' },
                { bg: 'rgba(14,165,233,0.07)', border: '#0ea5e9', text: '#38bdf8' },
              ];
              const pal = FLOOR_PALETTE[rp.color_index ?? idx % FLOOR_PALETTE.length];
              const cnt = computers.filter(c => c.room_id === rm.id).length;
              return (
                <div key={rm.id} style={{
                  position: 'absolute', left: rp.x, top: rp.y, width: rp.w, height: rp.h,
                  background: pal.bg, border: `1.5px solid ${pal.border}`,
                  borderRadius: 12, pointerEvents: 'none',
                }}>
                  <div style={{ padding: '5px 10px', borderBottom: `1px solid ${pal.border}22` }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: pal.text }}>{rm.name}</span>
                    <span style={{ fontSize: 10, color: pal.text + '88', marginLeft: 8 }}>{cnt} PC</span>
                  </div>
                </div>
              );
            })}

            {/* PC kartalar */}
            {computers.filter(c => c.pos_x != null).map(pc => {
              const busy = pc.status === 'busy' && !!pc.session_id;
              const booked = !!pc.next_booking;
              const broken = pc.is_broken || pc.status === 'maintenance';
              const offline = !pc.online;
              const mins = busy && pc.ends_at ? minsLeft(pc.ends_at) : null;
              const warning = mins !== null && mins < 5;
              const blink = warning && tick % 2 === 0;
              const deviceType = pc.type || 'pc';
              const dims = deviceType === 'billiard' ? { w: 130, h: 72 }
                         : deviceType === 'playstation' ? { w: 76, h: 76 }
                         : { w: 64, h: 66 };
              const typeCol = deviceType === 'billiard' ? '#10b981'
                            : deviceType === 'playstation' ? '#8b5cf6'
                            : null;
              const col = busy ? '#f97316' : broken ? '#ef4444' : booked ? '#f59e0b' : offline ? '#374151' : (typeCol || '#22c55e');
              const clientName = pc.user_name || pc.guest_name;
              const firstName = clientName ? clientName.split(' ')[0] : null;
              const deviceIcon = deviceType === 'billiard' ? '🎱' : deviceType === 'playstation' ? '🎮' : null;
              return (
                <div key={pc.id}
                  onClick={() => busy ? openModal('manage', pc, pc) : openModal('free', pc)}
                  style={{
                    position: 'absolute', left: +pc.pos_x, top: +pc.pos_y,
                    width: dims.w, height: dims.h,
                    background: busy ? 'rgba(249,115,22,0.22)' : broken ? 'rgba(239,68,68,0.22)' : booked ? 'rgba(245,158,11,0.18)' : offline ? 'rgba(20,20,32,0.7)' : 'rgba(34,197,94,0.14)',
                    border: `2px solid ${blink ? '#ef4444' : col}`,
                    borderRadius: 9, cursor: 'pointer', zIndex: 10,
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: 2, padding: '4px 2px',
                    boxShadow: offline && !busy ? 'none' : `0 0 14px ${col}66`,
                    opacity: offline && !busy && !booked ? 0.72 : 1,
                    transition: 'transform 0.1s', overflow: 'hidden',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.07)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {deviceIcon && <span style={{ fontSize: deviceType === 'billiard' ? 16 : 13, lineHeight: 1 }}>{deviceIcon}</span>}
                    {!deviceIcon && <div style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: pc.online ? '#22c55e' : '#4b5563', boxShadow: pc.online ? '0 0 5px #22c55e' : 'none' }} />}
                    <span style={{ fontWeight: 800, fontSize: 13, color: offline ? '#9ca3af' : '#fff', lineHeight: 1 }}>#{pc.number}</span>
                    {pc.client_type === 'app'    && <span style={{ fontSize: 8 }}>📱</span>}
                    {pc.client_type === 'walkin' && <span style={{ fontSize: 8 }}>💵</span>}
                  </div>
                  {busy && firstName && (
                    <div style={{ fontSize: 9, color: '#e2e8f0', lineHeight: 1, fontWeight: 600, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>{firstName}</div>
                  )}
                  {busy && pc.session_mode === 'hourly_open' && pc.started_at ? (
                    <>
                      <div style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, lineHeight: 1, color: '#f97316' }}>{fmtElapsed(pc.started_at)}</div>
                      {calcLiveCost(pc) != null && (
                        <div style={{ fontSize: 9, color: '#fbbf24', fontWeight: 700, lineHeight: 1, marginTop: 1 }}>{calcLiveCost(pc).toLocaleString()}so'm</div>
                      )}
                    </>
                  ) : busy && pc.ends_at ? (
                    <>
                      <div style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, lineHeight: 1, color: blink ? '#fff' : warning ? '#ef4444' : mins < 30 ? '#f97316' : '#22d3ee', background: blink ? '#ef4444' : 'transparent', borderRadius: 3, padding: blink ? '1px 3px' : 0, transition: 'background 0.2s' }}>{fmtTime(pc.ends_at)}</div>
                      {calcLiveCost(pc) != null && (
                        <div style={{ fontSize: 9, color: '#fbbf24', fontWeight: 700, lineHeight: 1, marginTop: 1 }}>{calcLiveCost(pc).toLocaleString()}so'm</div>
                      )}
                    </>
                  ) : busy && !pc.ends_at ? (
                    <div style={{ fontSize: 10, color: '#a78bfa' }}>⏱️</div>
                  ) : offline ? (
                    <div style={{ fontSize: 9, color: '#6b7280', lineHeight: 1, fontWeight: 600 }}>{t('statusOffline').toUpperCase()}</div>
                  ) : broken ? (
                    <div style={{ fontSize: 9, color: '#ef4444', lineHeight: 1, fontWeight: 700 }}>{t('statusMaintenance').toUpperCase()}</div>
                  ) : booked ? (
                    <div style={{ fontSize: 9, color: '#f59e0b', lineHeight: 1, fontWeight: 700 }}>{t('statusBooked').toUpperCase()}</div>
                  ) : (
                    <div style={{ fontSize: 9, color: '#22c55e', lineHeight: 1, fontWeight: 700 }}>{t('statusAvailableFloor').toUpperCase()}</div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      ) : (
        /* ── Grid ko'rinish (layout muharrirsiz) ── */
        <div style={S.grid}>
          {filteredComputers.map(pc => (
            <PcCard
              key={pc.id} pc={pc} tick={tick}
              onOpen={() => openModal('free', pc)}
              onManage={() => openModal('manage', pc, pc)}
              onOffline={() => openModal('free', pc)}
            />
          ))}
        </div>
      )}

      {/* ── MODAL: Sessiya ochish (ikkinchi qadam) ── */}
      {modal?.type === 'open' && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            {(() => {
              const dm = DEVICE_META[modal.computer.type || 'pc'] || DEVICE_META.pc;
              const isTm = modal.computer.type && modal.computer.type !== 'pc';
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                    <button onClick={() => setModal(m => ({ ...m, type: 'free' }))} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: 0 }}>←</button>
                    <div style={S.modalTitle}>{dm.icon} {dm.label} #{modal.computer.number} — {t('sessionOpenTitle')}</div>
                  </div>

                  {!isTm && (
                    <div style={{ display: 'flex', gap: 0, marginBottom: 18, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
                      {[['package', t('packagedMode')], ['hourly', t('hourlyMode')]].map(([mode, label]) => (
                        <button key={mode} onClick={() => setSessionMode(mode)} style={{
                          flex: 1, padding: '10px 0', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                          background: sessionMode === mode ? 'rgba(0,212,255,0.12)' : 'transparent',
                          border: 'none',
                          color: sessionMode === mode ? 'var(--cyan)' : 'var(--text3)',
                          borderRight: mode === 'package' ? '1px solid var(--border)' : 'none',
                        }}>{label}</button>
                      ))}
                    </div>
                  )}

                  <label style={S.label}>{t('phoneOptional')}</label>
                  <input style={S.input} placeholder="+998901234567"
                    value={clientPhone} onChange={e => searchClient(e.target.value)} />
                  {clientFound ? (
                    <div style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid var(--green)', borderRadius: 8, padding: '8px 12px', marginTop: -10, marginBottom: 14, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      ✓ <span style={{ color: 'var(--green)', fontWeight: 600 }}>{clientFound.name}</span>
                      <span style={{ color: 'var(--text3)' }}>{t('balanceLabel')}: {Number(clientFound.balance).toLocaleString()} so'm</span>
                    </div>
                  ) : clientPhone.length >= 9 ? (
                    <div style={{ marginTop: -10, marginBottom: 14 }}>
                      <input style={S.input} placeholder={t('guestNamePlaceholder')}
                        value={guestName} onChange={e => setGuestName(e.target.value)} />
                    </div>
                  ) : null}

                  {isTm || sessionMode === 'hourly' ? (
                    <>
                      {isTm && (
                        <>
                          {/* Rejim: hourly_open / package */}
                          <div style={{ display: 'flex', gap: 0, marginBottom: 14, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
                            {[['hourly_open', '⏱ Ochiq hisoblagich'], ['package', '📦 Paket (fixed)']].map(([mode, label]) => (
                              <button key={mode} onClick={() => { setStationMode(mode); setSelRate(''); }} style={{
                                flex: 1, padding: '9px 0', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                background: stationMode === mode ? 'rgba(124,58,237,0.15)' : 'transparent',
                                border: 'none', color: stationMode === mode ? 'var(--purple)' : 'var(--text3)',
                                borderRight: mode === 'hourly_open' ? '1px solid var(--border)' : 'none',
                              }}>{label}</button>
                            ))}
                          </div>
                          {/* Kishi soni (bilyard uchun) */}
                          {modal.computer.type === 'billiard' && (
                            <div style={{ marginBottom: 14 }}>
                              <label style={S.label}>👥 KISHI SONI</label>
                              <div style={{ display: 'flex', gap: 6 }}>
                                {[1, 2, 3, 4].map(n => (
                                  <button key={n} onClick={() => setPlayers(n)} style={{
                                    flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                                    fontSize: 13, fontWeight: 700,
                                    background: players === n ? 'rgba(124,58,237,0.2)' : 'var(--card2)',
                                    border: `1px solid ${players === n ? 'var(--purple)' : 'var(--border)'}`,
                                    color: players === n ? 'var(--purple)' : 'var(--text2)',
                                  }}>{n}</button>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Tarif tanlash */}
                          {stationRates.filter(r => r.station_type === modal.computer.type && r.active
                            && r.rate_type === (stationMode === 'package' ? 'package' : 'hourly')).length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                              <label style={S.label}>💰 TARIF</label>
                              <select style={S.select} value={selRate} onChange={e => setSelRate(e.target.value)}>
                                <option value="">-- Standart tarif --</option>
                                {stationRates
                                  .filter(r => r.station_type === modal.computer.type && r.active
                                    && r.rate_type === (stationMode === 'package' ? 'package' : 'hourly'))
                                  .map(r => (
                                    <option key={r.id} value={r.id}>
                                      {r.name} — {stationMode === 'package'
                                        ? `${Number(r.package_price).toLocaleString()} so'm / ${r.package_minutes} daq`
                                        : `${Number(r.hourly_rate).toLocaleString()} so'm/soat`}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          )}
                        </>
                      )}
                      {!isTm && modal.computer.hourly_rate && (
                        <div style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid var(--purple)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--text2)' }}>
                          {t('timedSessionInfo')}
                          <span style={{ color: 'var(--purple)', fontWeight: 600 }}> · {Number(modal.computer.hourly_rate).toLocaleString()} so'm/soat</span>
                        </div>
                      )}
                    </>
                  ) : (() => {
                    const pcRoomId = modal.computer.room_id ?? null;
                    // Paket bu xonaga tegishli bo'lsa ishlatish mumkin (room_ids massiv)
                    const canUse = p => {
                      const ids = p.room_ids || (p.room_id ? [p.room_id] : []);
                      return ids.length === 0 || ids.includes(pcRoomId) || ids.includes(Number(pcRoomId));
                    };
                    return (
                      <>
                        <label style={S.label}>{t('packageLabel')}</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                          {packages.map(p => {
                            const allowed = canUse(p);
                            const selected = selPkg === String(p.id);
                            return (
                              <button
                                key={p.id}
                                disabled={!allowed}
                                onClick={() => allowed && setSelPkg(String(p.id))}
                                style={{
                                  padding: '10px 14px', borderRadius: 10, textAlign: 'left',
                                  cursor: allowed ? 'pointer' : 'not-allowed',
                                  border: `2px solid ${selected ? 'var(--cyan)' : allowed ? 'var(--border)' : 'transparent'}`,
                                  background: selected ? 'rgba(0,212,255,0.1)' : allowed ? 'var(--card2)' : 'rgba(255,255,255,0.02)',
                                  opacity: allowed ? 1 : 0.38,
                                  transition: 'all 0.15s',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontWeight: 700, fontSize: 13, color: allowed ? 'var(--text)' : 'var(--text3)' }}>
                                    {p.name}
                                  </span>
                                  <span style={{ fontWeight: 800, fontSize: 14, color: selected ? 'var(--cyan)' : allowed ? 'var(--text)' : 'var(--text3)' }}>
                                    {Number(p.price).toLocaleString()} so'm
                                  </span>
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, display: 'flex', gap: 8 }}>
                                  <span>⏱ {p.duration_minutes} {t('minutesShort')}</span>
                                  {!allowed && <span style={{ color: '#ef4444' }}>— {t('roomNotAllowed')}</span>}
                                  {p.room_id == null && <span style={{ color: '#6b7280' }}>· {t('allRooms')}</span>}
                                </div>
                              </button>
                            );
                          })}
                          {packages.length === 0 && (
                            <div style={{ color: 'var(--text3)', fontSize: 12, padding: '10px 0' }}>{t('noPackagesFound')}</div>
                          )}
                        </div>
                        {/* Balansdan davom etish — faqat foydalanuvchi topilsa va balans bo'lsa */}
                        {clientFound && parseFloat(clientFound.balance) > 0 && selPkg && (
                          <div style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text2)' }}>
                              <input
                                type="checkbox"
                                checked={extendFromBalance}
                                onChange={e => setExtendFromBalance(e.target.checked)}
                                style={{ width: 15, height: 15, cursor: 'pointer' }}
                              />
                              {t('continueFromBalance')} ({Number(clientFound.balance).toLocaleString()} so'm)
                            </label>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {!isTm && (
                    <>
                      <label style={S.label}>{t('paymentTypeLabel')}</label>
                      <select style={S.select} value={payType} onChange={e => setPayType(e.target.value)}>
                        <option value="cash">{t('cashOption')}</option>
                        <option value="card">{t('cardOption')}</option>
                        <option value="balance">{t('balanceOption')}</option>
                      </select>
                    </>
                  )}

                  <div style={S.row}>
                    <button style={S.btnCancel} onClick={closeModal}>{t('cancelShort')}</button>
                    <button style={S.btnPrimary} onClick={handleOpenSession} disabled={saving || (!isTm && sessionMode === 'package' && !selPkg)}>
                      {saving ? '...' : t('startBtn')}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── MODAL: Sessiyani boshqarish ── */}
      {modal?.type === 'manage' && modal.session?.session_id && (() => {
        const dmM = DEVICE_META[modal.computer.type || 'pc'] || DEVICE_META.pc;
        return (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{dmM.icon} {dmM.label} #{modal.computer.number} — {t('manageTitle')}</div>

            {/* Vaqt tugayapti ogohlantirish */}
            {modal.session.ends_at && minsLeft(modal.session.ends_at) < 10 && (
              <div style={{
                background: 'rgba(239,68,68,0.12)', border: '1px solid #ef4444',
                borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#ef4444', fontSize: 14 }}>
                    {t('warningTimeEnding')} {fmtTime(modal.session.ends_at)} {t('minsLeft')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    {t('addTimeOrTopup')}
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: 'var(--card2)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={S.info}>{t('clientLabel')}:</span>
                <span style={S.infoVal}>{modal.session.user_name || modal.session.guest_name || t('guestCash')}</span>
                {modal.session.client_type === 'app' && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, background: 'rgba(129,140,248,0.15)', color: '#818cf8', border: '1px solid #818cf844', fontWeight: 600 }}>📱 APP</span>
                )}
                {modal.session.client_type === 'walkin' && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid #fbbf2444', fontWeight: 600 }}>💵 NAQD</span>
                )}
              </div>
              {modal.session.package_name ? (
                <>
                  <div style={S.info}>{t('packageField')}: <span style={S.infoVal}>{modal.session.package_name}</span></div>
                  <div style={S.info}>{t('timeLeftLabel')}: <span style={{ ...S.infoVal, color: minsLeft(modal.session.ends_at) < 10 ? '#ef4444' : 'var(--cyan)', fontFamily: 'Orbitron, sans-serif', fontWeight: 700 }}>{fmtTime(modal.session.ends_at)}</span></div>
                  <div style={S.info}>{t('paidLabel')}: <span style={S.infoVal}>{Number(modal.session.amount_paid).toLocaleString()} so'm</span></div>
                </>
              ) : (
                <>
                  <div style={S.info}>{t('typeField')}: <span style={{ ...S.infoVal, color: 'var(--purple)' }}>{t('timedType')}</span></div>
                  <div style={S.info}>{t('elapsedLabel')}: <span style={{ ...S.infoVal, color: 'var(--orange)', fontFamily: 'Orbitron, sans-serif' }}>{fmtElapsed(modal.session.started_at)}</span></div>
                  {modal.computer.hourly_rate && (
                    <div style={S.info}>{t('price') || 'Narx'}: <span style={S.infoVal}>{Number(modal.computer.hourly_rate).toLocaleString()} so'm/{t('hoursShort')}</span></div>
                  )}
                </>
              )}
              {modal.session.user_balance != null && (
                <div style={S.info}>{t('balanceLabel')}: <span style={{ ...S.infoVal, color: modal.session.user_balance < 5000 ? '#ef4444' : '#22c55e' }}>
                  {Number(modal.session.user_balance).toLocaleString()} so'm
                </span></div>
              )}
              <div style={S.info}>{t('payTypeLabel')}: <span style={S.infoVal}>{modal.session.payment_type === 'cash' ? t('cashLabel') : modal.session.payment_type === 'card' ? t('cardLabel') : t('appBalanceLabel')}</span></div>
            </div>

            {/* Balansga pul qo'shish / Qarz — faqat PC uchun */}
            {(!modal.computer.type || modal.computer.type === 'pc') && <div style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 600, letterSpacing: 1, marginBottom: 10 }}>
                💰 BALANSGA PUL QO'SHISH
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {[5000, 10000, 20000, 50000].map(amt => (
                  <button key={amt} onClick={() => setPayAmount(String(amt))} style={{
                    flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer', fontSize: 11,
                    background: payAmount === String(amt) ? 'rgba(0,212,255,0.15)' : 'var(--card2)',
                    border: `1px solid ${payAmount === String(amt) ? 'var(--cyan)' : 'var(--border)'}`,
                    color: payAmount === String(amt) ? 'var(--cyan)' : 'var(--text2)',
                  }}>{amt >= 1000 ? `${amt/1000}k` : amt}</button>
                ))}
              </div>
              <input
                value={payAmount}
                onChange={e => setPayAmount(e.target.value.replace(/\D/g, ''))}
                placeholder={t('amountPlaceholder')}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13,
                  background: 'var(--card2)', border: '1px solid var(--border)',
                  color: 'var(--text)', outline: 'none', boxSizing: 'border-box', marginBottom: 8,
                }}
              />
              {/* Preview: vaqt yoki qarz */}
              {payAmount ? topupIsDebt ? (
                <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8, padding: '6px 10px', background: 'rgba(239,68,68,0.08)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)' }}>
                  📋 {Number(payAmount).toLocaleString()} so'm qarz sifatida yoziladi
                </div>
              ) : (() => {
                const mins = calcMins(payAmount);
                return mins > 0 ? (
                  <div style={{ fontSize: 12, color: '#22c55e', marginBottom: 8, padding: '6px 10px', background: 'rgba(34,197,94,0.08)', borderRadius: 8, border: '1px solid rgba(34,197,94,0.2)' }}>
                    ✓ {Number(payAmount).toLocaleString()} so'm = <strong>+{mins} {t('minutesShort')}</strong>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8 }}>
                    {t('priceNotFound')}
                  </div>
                );
              })() : null}
              {/* To'lov usuli — faqat qarz emas bo'lsa */}
              {!topupIsDebt && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 8 }}>
                  {[
                    { key: 'cash',     label: '💵 Naqd',     color: 'var(--green)' },
                    { key: 'click',    label: '🔵 Click',    color: 'var(--blue)' },
                    { key: 'payme',    label: '📱 Payme',    color: 'var(--purple)' },
                    { key: 'terminal', label: '💳 Terminal', color: 'var(--cyan)' },
                  ].map(m => {
                    const active = payMethod === m.key;
                    return (
                      <button key={m.key} type="button" onClick={() => setPayMethod(m.key)} style={{
                        padding: '7px 0', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                        background: active ? `color-mix(in srgb, ${m.color} 15%, transparent)` : 'var(--card2)',
                        border: `1.5px solid ${active ? m.color : 'var(--border)'}`,
                        color: active ? m.color : 'var(--text2)',
                        transition: 'all 0.15s',
                      }}>{m.label}</button>
                    );
                  })}
                </div>
              )}
              {/* Qarz checkbox — faqat app foydalanuvchi uchun */}
              {modal.session.user_id && (
                <div
                  onClick={() => setTopupIsDebt(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
                    padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                    background: topupIsDebt ? 'rgba(239,68,68,0.1)' : 'var(--card2)',
                    border: `1.5px solid ${topupIsDebt ? '#ef4444' : 'var(--border)'}`,
                    transition: 'all 0.15s',
                  }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${topupIsDebt ? '#ef4444' : 'var(--border)'}`,
                    background: topupIsDebt ? '#ef4444' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {topupIsDebt && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
                  </div>
                  <div style={{ fontSize: 12, color: topupIsDebt ? '#ef4444' : 'var(--text2)', fontWeight: topupIsDebt ? 700 : 400 }}>
                    Qarz sifatida yozish
                  </div>
                </div>
              )}
              <button
                onClick={handlePayAndAddTime}
                disabled={saving || (!topupIsDebt && !calcMins(payAmount)) || (topupIsDebt && !payAmount) || (topupIsDebt && !modal.session.user_id)}
                style={{
                  width: '100%', padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                  background: topupIsDebt ? 'rgba(239,68,68,0.15)' : 'rgba(255,107,53,0.1)',
                  border: `1px solid ${topupIsDebt ? '#ef4444' : 'var(--orange)'}`,
                  color: topupIsDebt ? '#ef4444' : 'var(--orange)',
                }}>
                {saving ? '...' : topupIsDebt
                  ? `📋 Qarz: ${payAmount ? Number(payAmount).toLocaleString() : 0} so'm`
                  : calcMins(payAmount) ? `+ ${calcMins(payAmount)} ${t('minutesShort')}` : t('enterAmount')}
              </button>
            </div>}

            {/* Ko'chirish */}
            {availablePCs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {(() => {
                  const mt = modal.computer?.type || 'pc';
                  const dm = DEVICE_META[mt] || DEVICE_META.pc;
                  return (
                    <>
                      <label style={S.label}>{dm.icon} BOSHQA {dm.label.toUpperCase()}GA KO'CHIRISH</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <select style={{ ...S.select, marginBottom: 0, flex: 1 }}
                          value={moveTo} onChange={e => setMoveTo(e.target.value)}>
                          <option value="">{dm.label} tanlang...</option>
                          {availablePCs.map(c => <option key={c.id} value={c.id}>{dm.icon} {dm.label} #{c.number}</option>)}
                        </select>
                        <button style={{ ...S.btnPrimary, flex: 'none', padding: '12px 16px' }}
                          onClick={handleMove} disabled={saving || !moveTo}>{t('moveBtn')}</button>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            <hr style={S.divider} />

            {/* stopResult — tugallangan sessiya hisob-kitob */}
            {stopResult ? (
              <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid var(--green)', borderRadius: 14, padding: 20, marginBottom: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 700, marginBottom: 8 }}>✓ SESSIYA YAKUNLANDI</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>{Number(stopResult.amount_paid).toLocaleString()} so'm</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>
                  {stopResult.elapsed_min} daq · {stopResult.players} kishi
                  {stopResult.tip_amount > 0 && ` · Peshka: ${Number(stopResult.tip_amount).toLocaleString()} so'm`}
                </div>
                <button style={{ ...S.btnPrimary, marginTop: 14 }} onClick={closeModal}>OK</button>
              </div>
            ) : (
              <>
                {(modal.computer.type && modal.computer.type !== 'pc') && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={S.label}>💳 TO'LOV TURI</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[['cash', '💵 Naqd'], ['card', '💳 Karta'], ['online', '📱 Online']].map(([val, label]) => (
                        <button key={val} onClick={() => setPayType(val)} style={{
                          flex: 1, padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                          background: payType === val ? 'rgba(0,212,255,0.15)' : 'var(--card2)',
                          border: `1px solid ${payType === val ? 'var(--cyan)' : 'var(--border)'}`,
                          color: payType === val ? 'var(--cyan)' : 'var(--text2)',
                        }}>{label}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div style={S.row}>
                  <button style={S.btnCancel} onClick={closeModal}>{t('close')}</button>
                  <button style={S.btnDanger} onClick={handleStop} disabled={saving}>
                    {saving ? '...' : t('stopSessionBtn')}
                  </button>
                </div>
              </>
            )}

            {(!modal.computer.type || modal.computer.type === 'pc') && (<>
            <hr style={S.divider} />
            <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: 1, marginBottom: 8 }}>{t('remoteControl')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button style={{ ...S.cardBtn('0,212,255'), flex: 'none', padding: '8px 0' }} disabled={cmdSending}
                onClick={() => sendCommand(modal.computer.id, 'lock')}>🔒 Lock</button>
              <button style={{ ...S.cardBtn('0,212,255'), flex: 'none', padding: '8px 0' }} disabled={cmdSending}
                onClick={() => sendCommand(modal.computer.id, 'unlock')}>🔓 Unlock</button>
              <button style={{ ...S.cardBtn('255,107,53'), flex: 'none', padding: '8px 0' }} disabled={cmdSending}
                onClick={() => { if (window.confirm('PC qayta yoqilsinmi?')) sendCommand(modal.computer.id, 'restart'); }}>↺ Restart</button>
              <button style={{ ...S.cardBtn('255,59,92'), flex: 'none', padding: '8px 0' }} disabled={cmdSending}
                onClick={() => { if (window.confirm('PC o\'chirilsinmi?')) sendCommand(modal.computer.id, 'shutdown'); }}>⏻ Shutdown</button>
            </div>
            {modal.computer.mac_address && (
              <button style={{ width: '100%', marginTop: 8, padding: '8px 0', fontSize: 12, borderRadius: 8, cursor: 'pointer', background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)', color: 'var(--green)' }}
                onClick={() => sendWoL(modal.computer.id)}>⚡ Wake-on-LAN (yoqish)</button>
            )}
            </>)}
          </div>
        </div>
        );
      })()}

      {/* ── MODAL: Sessiya yo'q — manage bosib kelganda ── */}
      {modal?.type === 'manage' && !modal.session?.session_id && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>PC #{modal.computer.number}</div>
            <div style={{ color: 'var(--text2)', marginBottom: 20 }}>{t('emptyPC')}</div>
            <div style={S.row}>
              <button style={S.btnCancel} onClick={closeModal}>{t('close')}</button>
              <button style={S.btnPrimary} onClick={() => setModal(m => ({ ...m, type: 'free' }))}>
                {t('manageArrow')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Bo'sh PC (asosiy: yoqish/o'chirish) ── */}
      {modal?.type === 'free' && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            {/* Sarlavha */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: modal.computer.online ? '#22c55e' : '#4b5563',
                boxShadow: modal.computer.online ? '0 0 6px #22c55e' : 'none',
              }} />
              <div style={S.modalTitle}>
                PC #{modal.computer.number} — {modal.computer.online ? t('pcOnline') : t('pcOffline')}
              </div>
              {modal.computer.is_broken && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid #ef444433' }}>🔧 Texnik</span>
              )}
            </div>

            {/* MAVJUD BRON */}
            {modal.computer.next_booking && (() => {
              const bk = modal.computer.next_booking;
              const from = new Date(bk.scheduled_at);
              const to   = new Date(bk.scheduled_to);
              const fmt  = d => d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
              const fmtD = d => d.toLocaleDateString('uz-UZ', { day: 'numeric', month: 'short' });
              return (
                <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b', borderRadius: 12, padding: '12px 14px', marginBottom: 4 }}>
                  <div style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, letterSpacing: 1, marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span>🔖 BRON</span>
                    {bk.source === 'bulk' ? (
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 6, background: 'rgba(155,92,255,0.18)', color: '#9B5CFF', border: '1px solid #9B5CFF80' }}>📱 MOBIL ILOVADAN</span>
                    ) : (
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 6, background: 'rgba(39,224,255,0.18)', color: '#27E0FF', border: '1px solid #27E0FF80' }}>🏢 ADMIN PANELIDAN</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 15 }}>{bk.user_name || 'Noma\'lum'}</span>
                    {bk.is_guest
                      ? <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 6, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid #fbbf2444' }}>mehmон</span>
                      : <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 6, background: 'rgba(129,140,248,0.15)', color: '#818cf8', border: '1px solid #818cf844' }}>📱 app</span>
                    }
                  </div>
                  {bk.user_phone && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>{bk.user_phone}</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 18 }}>⏰</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fmtD(from)} · {fmt(from)} → {fmt(to)}</div>
                      {bk.package_name && <div style={{ fontSize: 11, color: 'var(--cyan)' }}>{bk.package_name}</div>}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* YOQISH / O'CHIRISH — faqat PC uchun */}
              {(!modal.computer.type || modal.computer.type === 'pc') && (<>
              <button
                onClick={() => {
                  if (modal.computer.mac_address) { sendWoL(modal.computer.id); closeModal(); }
                  else alert('Bu PC uchun MAC manzil kiritilmagan.\nMuharrir → PC sozlamalari dan kiriting.');
                }}
                style={{
                  padding: '16px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: '#fff', fontSize: 16, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  opacity: modal.computer.mac_address ? 1 : 0.6,
                }}>
                {t('turnOnBtn')}
              </button>
              <button
                onClick={() => { if (window.confirm('PC o\'chirilsinmi?')) { sendCommand(modal.computer.id, 'shutdown'); closeModal(); } }}
                style={{
                  padding: '16px', borderRadius: 12, border: '1px solid #ef4444', cursor: 'pointer',
                  background: 'rgba(239,68,68,0.1)',
                  color: '#ef4444', fontSize: 16, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  opacity: modal.computer.online ? 1 : 0.6,
                }}>
                {t('turnOffBtn')}
              </button>
              </>)}

              {/* Sessiya ochish — ikkinchi o'rinda */}
              {!modal.computer.is_broken && (
                <button
                  onClick={() => setModal(m => ({ ...m, type: 'open' }))}
                  style={{
                    padding: '11px', borderRadius: 10, border: '1px solid var(--border)',
                    background: 'var(--card2)', color: 'var(--text2)',
                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  {t('openSessionAction')}
                </button>
              )}

              {/* Bron qo'shish */}
              {!modal.computer.is_broken && (
                <button
                  onClick={() => setModal(m => ({ ...m, type: 'book' }))}
                  style={{
                    padding: '11px', borderRadius: 10, border: '1px dashed #f59e0b',
                    background: 'rgba(245,158,11,0.06)', color: '#f59e0b',
                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  {t('addBookingAction')}
                </button>
              )}

              {/* Texnik / Faollashtirish */}
              <button
                onClick={() => setMaintenance(modal.computer.id, !modal.computer.is_broken)}
                style={{
                  padding: '10px', borderRadius: 10, cursor: 'pointer', fontSize: 12,
                  background: 'transparent',
                  border: `1px solid ${modal.computer.is_broken ? 'var(--orange)' : '#374151'}`,
                  color: modal.computer.is_broken ? 'var(--orange)' : '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                {modal.computer.is_broken ? t('maintenanceActivate') : t('maintenanceSet')}
              </button>

              <button style={S.btnCancel} onClick={closeModal}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Bron qo'shish ── */}
      {modal?.type === 'book' && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <button onClick={() => setModal(m => ({ ...m, type: 'free' }))} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: 0 }}>←</button>
              <div style={S.modalTitle}>🔖 PC #{modal.computer.number} — {t('bookTitle')}</div>
            </div>

            <label style={S.label}>{t('guestNameLabel')}</label>
            <input style={S.input} placeholder={t('guestNamePlh')}
              value={bookName} onChange={e => setBookName(e.target.value)} />

            <label style={S.label}>{t('phoneWithLookup')}</label>
            <input style={S.input} placeholder="+998901234567"
              value={bookPhone} onChange={e => setBookPhone(e.target.value)} />

            <label style={S.label}>{t('dateLabel2')}</label>
            <input type="date" style={S.input}
              value={bookDate} onChange={e => setBookDate(e.target.value)}
              min={new Date().toISOString().slice(0,10)} />

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>{t('fromTimeLabel')}</label>
                <input type="time" style={S.input}
                  value={bookFrom} onChange={e => setBookFrom(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>{t('toTimeLabel')}</label>
                <input type="time" style={S.input}
                  value={bookTo} onChange={e => setBookTo(e.target.value)} />
              </div>
            </div>

            {packages.length > 0 && (
              <>
                <label style={S.label}>{t('packageOptional')}</label>
                <select style={S.select} value={bookPkg} onChange={e => setBookPkg(e.target.value)}>
                  <option value="">{t('withoutPackage')}</option>
                  {packages.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.duration_minutes} {t('minutesShort')})</option>
                  ))}
                </select>
              </>
            )}

            <div style={S.row}>
              <button style={S.btnCancel} onClick={closeModal}>{t('cancelShort')}</button>
              <button style={S.btnPrimary} onClick={handleCreateBooking}
                disabled={bookSaving || !bookDate || !bookFrom}>
                {bookSaving ? '...' : t('bookAction')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DEVICE_META = {
  pc:          { icon: '🖥️', label: 'PC' },
  billiard:    { icon: '🎱', label: 'Bilyard' },
  playstation: { icon: '🎮', label: 'PlayStation' },
  console:     { icon: '🕹️', label: 'Konsol' },
};

function fmtElapsed(startedAt) {
  const diff = Date.now() - new Date(startedAt);
  const total = Math.floor(diff / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function calcLiveCost(pc) {
  if (!pc.started_at) return null;
  const rate = parseFloat(pc.station_hourly_rate || pc.hourly_rate);
  if (!rate) return null;
  const elapsedHours = Math.max(0, Date.now() - new Date(pc.started_at)) / 3600000;
  const perExtra = parseFloat(pc.station_per_player_extra || 0);
  const extraPlayers = Math.max(0, (pc.players || 1) - 1);
  return Math.round(elapsedHours * rate + extraPlayers * perExtra * elapsedHours);
}

// ── PC Karta komponenti ─────────────────────────────────────────────────────
function PcCard({ pc, tick, onOpen, onManage, onOffline }) {
  const { t } = useLang();
  const isBusy = pc.status === 'busy' && pc.session_id;
  const isFree = pc.status === 'available';
  const isOffline = !pc.online;
  const isBooked = isFree && !!pc.next_booking;
  const deviceType = pc.type || 'pc';
  const dm = DEVICE_META[deviceType] || DEVICE_META.pc;
  const isTimed = deviceType !== 'pc';

  return (
    <div style={S.card(pc.status, isBooked)} onClick={isBusy ? onManage : isOffline ? onOffline : isFree ? onOpen : undefined}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={S.pcNum}>{dm.icon} {dm.label} #{String(pc.number).padStart(2, '0')}</div>
        <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, fontWeight: 600,
          background: pc.online ? 'rgba(0,255,136,0.15)' : 'rgba(136,136,136,0.12)',
          color: pc.online ? 'var(--green)' : 'var(--text3)',
          border: `1px solid ${pc.online ? 'var(--green)' : 'var(--border)'}`,
        }}>
          {pc.online ? '● online' : '○ offline'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={S.statusDot(pc.status, isBooked)} />
        <span style={S.statusLabel(pc.status, isBooked)}>
          {isBusy ? t('statusBusyFloor') : isBooked ? t('statusBooked') : isFree ? t('statusAvailableFloor') : t('statusBrokenFloor')}
        </span>
      </div>
      {isBooked && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 10, color: 'var(--orange)', display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{pc.next_booking.user_name} · {new Date(pc.next_booking.scheduled_at).toLocaleTimeString('uz-UZ', {hour:'2-digit',minute:'2-digit'})}</span>
            {pc.next_booking.source === 'bulk' ? (
              <span style={{ fontSize: 8, padding: '0px 4px', borderRadius: 4, background: 'rgba(155,92,255,0.18)', color: '#9B5CFF', border: '1px solid #9B5CFF80', fontWeight: 700 }}>📱 MOBIL</span>
            ) : (
              <span style={{ fontSize: 8, padding: '0px 4px', borderRadius: 4, background: 'rgba(39,224,255,0.18)', color: '#27E0FF', border: '1px solid #27E0FF80', fontWeight: 700 }}>🏢 ADMIN</span>
            )}
          </div>
          {pc.next_booking.user_phone && (
            <div style={{ fontSize: 9, color: 'var(--text2)', marginTop: 2 }}>📞 {pc.next_booking.user_phone}</div>
          )}
        </div>
      )}

      {isBusy && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, marginBottom: 2 }}>
            <span style={S.userName}>{pc.user_name || pc.guest_name || t('guestCash')}</span>
            {pc.client_type === 'app' && (
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, background: 'rgba(129,140,248,0.15)', color: '#818cf8', border: '1px solid #818cf844', fontWeight: 600, whiteSpace: 'nowrap' }}>📱 APP</span>
            )}
            {pc.client_type === 'walkin' && (
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid #fbbf2444', fontWeight: 600, whiteSpace: 'nowrap' }}>💵 NAQD</span>
            )}
          </div>
          {pc.user_phone && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>{pc.user_phone}</div>
          )}
          {isTimed ? (
            <>
              <div style={{ fontSize: 11, color: 'var(--purple)', marginTop: 2 }}>{t('timedMode')}</div>
              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 18, fontWeight: 700, marginTop: 8, color: 'var(--orange)' }}>
                {fmtElapsed(pc.started_at)}
              </div>
            </>
          ) : (
            <>
              <div style={S.pkgName}>{pc.package_name}</div>
              <div style={S.timeLeft(minsLeft(pc.ends_at))}>{fmtTime(pc.ends_at)}</div>
            </>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <button
              style={{ flex: 1, padding: '6px 0', fontSize: 11, borderRadius: 8, cursor: 'pointer', background: 'rgba(255,107,53,0.1)', border: '1px solid var(--orange)', color: 'var(--orange)' }}
              onClick={e => { e.stopPropagation(); onManage(); }}>
              {t('manageBtn')}
            </button>
          </div>
        </>
      )}

      {isFree && (
        <button
          style={{ width: '100%', marginTop: 12, padding: '8px 0', fontSize: 12, borderRadius: 8, cursor: 'pointer', background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)', color: 'var(--green)' }}
          onClick={e => { e.stopPropagation(); onOpen(); }}>
          {isTimed ? t('freeStartBtn') : t('freeSessionBtn')}
        </button>
      )}
    </div>
  );
}
